import { useContext, useEffect, useRef, useState } from 'react';
import {
  BookIcon,
  ImagePlusIcon,
  MaximizeIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from 'assets/icons';
import { Button, ButtonSize, ButtonStyle } from 'common/button';
import { QrCodeCard } from 'common/qr-code-card';
import { AlbumContentType, NotificationType } from 'common/enums';
import { appActions, AppContext } from 'context/app-context';
import { directUploadFile } from 'helpers/direct-upload';
import { memoryQrCodes } from 'helpers/memories';
import { adaptContentType, classnames } from 'helpers/utils';
import { NotificationObject } from 'models/notificationObject';
import { VaultsController } from 'networking/controllers/vaults-controller';
import {
  AlbumContentSerialized,
  AlbumDetailType,
} from 'networking/types/album-detail';
import { CenterModal } from '../center-modal';
import { ThumbnailCropModal } from '../thumbnail-crop-modal';
import { MemoryTags } from './memory-tags';
import styles from './details-panel.module.scss';

// What a video's custom thumbnail may be. The API also takes HEIC, but most
// browsers can't show one, so it isn't offered here.
const THUMBNAIL_ACCEPT =
  '.jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp';
const THUMBNAIL_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const THUMBNAIL_MAX_BYTES = 30 * 1024 * 1024;
// Used until the video's own shape is known, or when it can't be read.
const DEFAULT_VIDEO_ASPECT = 16 / 9;

// A video's width / height, read from its metadata (the file isn't
// downloaded). Falls back to 16:9.
const readVideoAspect = (url: string) =>
  new Promise<number>((resolve) => {
    const video = document.createElement('video');
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const done = (aspect: number) => {
      clearTimeout(timeout);
      video.removeAttribute('src');
      video.load();
      resolve(aspect);
    };
    timeout = setTimeout(() => done(DEFAULT_VIDEO_ASPECT), 8000);
    video.preload = 'metadata';
    video.muted = true;
    video.onloadedmetadata = () =>
      done(
        video.videoWidth && video.videoHeight
          ? video.videoWidth / video.videoHeight
          : DEFAULT_VIDEO_ASPECT,
      );
    video.onerror = () => done(DEFAULT_VIDEO_ASPECT);
    video.src = url;
  });

interface DetailsPanelProps {
  album: AlbumDetailType;
  content: AlbumContentSerialized | undefined;
  onContentUpdated: (
    contentId: number,
    partial: Partial<AlbumContentSerialized>,
  ) => void;
  // Phones: the fields under the memory, as in the mobile app (description
  // first, "Albums"); the location and date are under the title there.
  phone?: boolean;
}

// The selected memory's details. Its location and date are the memory's own;
// the album's are set on the album page.
export const DetailsPanel = ({
  album,
  content,
  onContentUpdated,
  phone = false,
}: DetailsPanelProps) => {
  const { dispatch } = useContext(AppContext);
  const [descriptionDraft, setDescriptionDraft] = useState('');
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [showThumbnail, setShowThumbnail] = useState(false);
  const [savingThumbnail, setSavingThumbnail] = useState(false);
  // An image picked for the thumbnail, waiting to be cropped.
  const [cropFile, setCropFile] = useState<File | null>(null);
  const [videoAspect, setVideoAspect] = useState(DEFAULT_VIDEO_ASPECT);
  const thumbnailInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setDescriptionDraft(content?.description ?? '');
  }, [content?.id, content?.description]);

  const notify = (title: string, type: NotificationType, message?: string) =>
    dispatch({
      type: appActions.NOTIFICATION,
      notification: new NotificationObject({
        show: true,
        title,
        message,
        type,
      }),
    });

  const handleSaveDescription = async () => {
    if (!content) return;
    try {
      await VaultsController.updateContentMetadata(content.id, {
        description: descriptionDraft.trim(),
      });
      onContentUpdated(content.id, { description: descriptionDraft.trim() });
      setShowDescriptionModal(false);
      notify('Description updated', NotificationType.Success);
    } catch (error) {
      notify('The description could not be updated', NotificationType.Error);
    }
  };

  // A video's custom thumbnail: shown on its card and as the player's poster
  // instead of a frame of the video.
  // A picked image is checked, then cropped to the video's shape before it's
  // uploaded.
  const handleThumbnailPicked = (file: File | undefined) => {
    if (!content || !file) return;
    if (!THUMBNAIL_TYPES.includes(file.type)) {
      notify(
        'This image can’t be used',
        NotificationType.Error,
        'Choose a JPG, PNG or WebP image.',
      );
      return;
    }
    if (file.size > THUMBNAIL_MAX_BYTES) {
      notify(
        'This image is too large',
        NotificationType.Error,
        'Choose an image under 30 MB.',
      );
      return;
    }
    setCropFile(file);
  };

  const saveThumbnail = async (cropped: File) => {
    if (!content) return;
    setSavingThumbnail(true);
    try {
      const { signedId } = await directUploadFile(cropped);
      await VaultsController.updateContentMetadata(content.id, {
        customThumbnailSignedId: signedId,
      });
      // Show the cropped image right away; the server's copy comes with the
      // next load.
      const url = URL.createObjectURL(cropped);
      onContentUpdated(content.id, {
        customThumbnailUrl: url,
        thumbnailUrl: url,
      });
      setCropFile(null);
      notify('Thumbnail updated', NotificationType.Success);
    } catch (error) {
      notify('The thumbnail could not be updated', NotificationType.Error);
    } finally {
      setSavingThumbnail(false);
    }
  };

  const handleRemoveThumbnail = async () => {
    if (!content) return;
    setSavingThumbnail(true);
    try {
      await VaultsController.updateContentMetadata(content.id, {
        customThumbnailSignedId: null,
      });
      // Back to the recording's own frame; an uploaded video has none, and
      // its card then shows a frame from the video itself.
      onContentUpdated(content.id, {
        customThumbnailUrl: null,
        thumbnailUrl: content.content?.thumbnailUrl ?? '',
      });
      setShowThumbnail(false);
      notify('Thumbnail removed', NotificationType.Success);
    } catch (error) {
      notify('The thumbnail could not be removed', NotificationType.Error);
    } finally {
      setSavingThumbnail(false);
    }
  };

  const pickThumbnail = () => thumbnailInputRef.current?.click();

  const isVideo =
    !!content && adaptContentType(content) === AlbumContentType.video;
  const customThumbnailUrl = content?.customThumbnailUrl;
  const videoUrl = isVideo ? content?.mediaUrl : undefined;

  // The video's shape, for the crop frame and the thumbnail preview.
  useEffect(() => {
    if (!videoUrl) return undefined;
    let cancelled = false;
    setVideoAspect(DEFAULT_VIDEO_ASPECT);
    readVideoAspect(videoUrl).then((aspect) => {
      if (!cancelled) setVideoAspect(aspect);
    });
    return () => {
      cancelled = true;
    };
  }, [videoUrl]);

  // Printed QR codes that open this memory.
  const qrCodes = content ? memoryQrCodes(content) : [];

  // Journal/text entries don't carry a separate description field.
  const showDescription =
    !!content && adaptContentType(content) !== AlbumContentType.write;

  // What the viewer may change on this memory (the API says; owner pages
  // carry everything). Viewers see the details without the edit buttons.
  const canEdit = content?.myAccess?.canEdit ?? true;
  const canDescribe = content?.myAccess?.canDescribe ?? true;

  const openDescriptionEditor = () => {
    setDescriptionDraft(content?.description ?? '');
    setShowDescriptionModal(true);
  };

  return (
    <div className={classnames(styles.panel, { [styles.panelPage]: phone })}>
      {!phone && (
        <>
          <MemoryTags
            content={content}
            onContentUpdated={onContentUpdated}
            readOnly={!canEdit}
          />
          <div className={styles.divider} />
        </>
      )}

      <div className={classnames(styles.field, styles.albumField)}>
        <span className={styles.label}>{phone ? 'Albums' : 'Album'}</span>
        <div className={styles.albumChip}>
          <BookIcon className={styles.albumChipIcon} />
          <span className={styles.albumChipText}>{album.name}</span>
        </div>
      </div>

      {isVideo && (canEdit || customThumbnailUrl) && (
        <div className={classnames(styles.field, styles.thumbnailField)}>
          <div className={styles.fieldHeader}>
            <span className={styles.label}>Thumbnail</span>
            {canEdit && customThumbnailUrl && (
              <div className={styles.fieldActions}>
                <button
                  type="button"
                  className={styles.editButton}
                  onClick={pickThumbnail}
                  disabled={savingThumbnail}
                >
                  <PencilIcon className={styles.editIcon} />
                  <span>Change</span>
                </button>
                <button
                  type="button"
                  className={styles.iconButton}
                  aria-label="Remove thumbnail"
                  onClick={handleRemoveThumbnail}
                  disabled={savingThumbnail}
                >
                  <TrashIcon className={styles.editIcon} />
                </button>
              </div>
            )}
          </div>
          {customThumbnailUrl ? (
            // Clicking the thumbnail opens it full screen.
            <button
              type="button"
              className={styles.thumbnailPreview}
              style={{ aspectRatio: String(videoAspect) }}
              aria-label="View thumbnail full screen"
              onClick={() => setShowThumbnail(true)}
            >
              <img src={customThumbnailUrl} alt="" />
              <span className={styles.thumbnailExpand}>
                <MaximizeIcon />
              </span>
            </button>
          ) : (
            canEdit && (
              <button
                type="button"
                className={styles.addDescriptionButton}
                onClick={pickThumbnail}
                disabled={savingThumbnail}
              >
                <ImagePlusIcon className={styles.addDescriptionIcon} />
                <span>
                  {savingThumbnail ? 'Uploading…' : 'Add a thumbnail'}
                </span>
              </button>
            )
          )}
          <input
            ref={thumbnailInputRef}
            type="file"
            accept={THUMBNAIL_ACCEPT}
            className={styles.hiddenInput}
            onChange={(e) => {
              handleThumbnailPicked(e.target.files?.[0]);
              // Let the same file be picked again after a failure.
              e.target.value = '';
            }}
          />
        </div>
      )}

      {showDescription && (canDescribe || content?.description) && (
        <div className={classnames(styles.field, styles.descriptionField)}>
          <div className={styles.fieldHeader}>
            <span className={styles.label}>Description</span>
            {/* Phones always offer Edit, as in the mobile app. */}
            {canDescribe && (content?.description || phone) && (
              <button
                type="button"
                className={styles.editButton}
                aria-label="Edit description"
                onClick={openDescriptionEditor}
              >
                <PencilIcon className={styles.editIcon} />
                <span>Edit</span>
              </button>
            )}
          </div>
          {content?.description && !canDescribe && (
            <p className={styles.descriptionText}>{content.description}</p>
          )}
          {content?.description && canDescribe && (
            // The text itself is also a target: clicking it opens the editor.
            <button
              type="button"
              className={styles.descriptionText}
              onClick={openDescriptionEditor}
            >
              {content.description}
            </button>
          )}
          {!content?.description && canDescribe && (
            <button
              type="button"
              className={styles.addDescriptionButton}
              onClick={openDescriptionEditor}
            >
              <PlusIcon className={styles.addDescriptionIcon} />
              <span>Add a description</span>
            </button>
          )}
        </div>
      )}

      {qrCodes.length > 0 && (
        <div className={classnames(styles.field, styles.qrField)}>
          <span className={styles.label}>QR code</span>
          {qrCodes.map((qrCode) => (
            <QrCodeCard key={qrCode.id} qrCode={qrCode} />
          ))}
        </div>
      )}

      {cropFile && (
        <ThumbnailCropModal
          file={cropFile}
          aspect={videoAspect}
          saving={savingThumbnail}
          onCancel={() => setCropFile(null)}
          onConfirm={saveThumbnail}
        />
      )}

      {showThumbnail && customThumbnailUrl && (
        <CenterModal
          onClose={() => setShowThumbnail(false)}
          className={styles.thumbnailViewer}
        >
          <img
            src={customThumbnailUrl}
            alt={`${content?.name ?? 'Video'} thumbnail`}
            className={styles.thumbnailFull}
          />
        </CenterModal>
      )}

      {showDescriptionModal && (
        <CenterModal
          onClose={() => setShowDescriptionModal(false)}
          className={styles.descriptionCard}
        >
          <div className={styles.modalBody}>
            <span className={styles.modalTitle}>Description</span>
            <textarea
              className={styles.descriptionInput}
              value={descriptionDraft}
              placeholder="Add a description"
              onChange={(e) => setDescriptionDraft(e.target.value)}
            />
            <div className={styles.descriptionActions}>
              <Button
                buttonStyle={ButtonStyle.SecondaryGray}
                buttonSize={ButtonSize.Medium}
                onClick={() => setShowDescriptionModal(false)}
              >
                Discard
              </Button>
              <Button
                buttonStyle={ButtonStyle.Primary}
                buttonSize={ButtonSize.Medium}
                onClick={handleSaveDescription}
              >
                Save
              </Button>
            </div>
          </div>
        </CenterModal>
      )}
    </div>
  );
};
